// ══════════════════════════════════════════════════════════
// CHECKOUT TYPES
//
// Tipos para el flujo de checkout y suscripciones
// hacia el backend PHP. Nada de esto escribe directamente
// en Supabase ni en ningún procesador de pagos.
//
// FRONTEND NO PUEDE:
// - enviar price, currency, payment_status,
//   subscription_status, provider_reference
// - marcar pagos como completados
// - activar suscripciones anticipadamente
// ══════════════════════════════════════════════════════════

// ── STREAMING MODALITY ──
// Debe coincidir con RadioFormData.streamingMode

export type StreamingMode = 'EXTERNAL_STREAM' | 'RFW_MANAGED';

// ── CHECKOUT ──

export type CheckoutStatus =
  | 'PENDING'
  | 'REQUIRES_PAYMENT'
  | 'PROCESSING'
  | 'PAID'
  | 'FAILED'
  | 'CANCELLED'
  | 'EXPIRED';

export type BillingPeriod = 'monthly' | 'yearly';

/**
 * Payload que el frontend envía al backend para crear un checkout.
 *
 * PROHIBIDO enviar:
 * - price
 * - currency
 * - payment_status
 * - subscription_status
 * - provider_reference
 * - organization_id (el backend lo obtiene del token)
 * - profile_id
 * - discount arbitrario
 *
 * PERMITIDO enviar únicamente:
 * - plan_id
 * - radio_id (opcional)
 * - billing_period
 * - success_url autorizada
 * - cancel_url autorizada
 */
export interface CheckoutRequest {
  plan_id: string;
  radio_id?: string;
  billing_period: BillingPeriod;
  success_url: string;
  cancel_url: string;
}

/**
 * Respuesta del backend al crear un checkout.
 * Contiene la URL segura a la que el usuario debe ser redirigido.
 */
export interface CheckoutResponse {
  checkout_id: string;
  checkout_url: string;
  status: CheckoutStatus;
  expires_at: string;
  created_at: string;
}

/**
 * Estado completo de un checkout consultado por ID.
 */
export interface CheckoutStatusResponse {
  checkout_id: string;
  plan_id: string;
  billing_period: BillingPeriod;
  status: CheckoutStatus;
  checkout_url: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
  /** Solo presente cuando el backend confirma PAID */
  subscription_id?: string;
}

// ── PLAN (desde backend) ──

/**
 * Plan devuelto por GET /api/v1/plans.
 * Precio y moneda vienen del backend — NUNCA del frontend.
 */
export interface BackendPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_monthly: number;
  price_yearly: number | null;
  currency: string;
  features: Record<string, string | number | boolean>;
  is_active: boolean;
  /** Modalidades de streaming compatibles con este plan */
  streaming_modes: StreamingMode[];
  sort_order: number;
}

// ── SUBSCRIPTION (desde backend) ──

export type BackendSubscriptionStatus =
  | 'active'
  | 'pending'
  | 'past_due'
  | 'cancelled'
  | 'expired'
  | 'trial';

/**
 * Suscripción devuelta por GET /api/v1/me/subscription.
 * Solo lectura desde frontend.
 */
export interface BackendSubscription {
  id: string;
  plan_id: string;
  plan_name: string;
  status: BackendSubscriptionStatus;
  starts_at: string | null;
  ends_at: string | null;
  auto_renew: boolean;
  billing_period: BillingPeriod;
  created_at: string;
}

// ── PLAN COMPATIBILITY ──

export interface PlanCompatibilityResult {
  compatible: boolean;
  reason?: string;
}

/**
 * Planes que incluyen capacidad de streaming administrado.
 * Debe coincidir con la lógica del backend.
 */
export const STREAMING_CAPABLE_PLAN_SLUGS = ['pro', 'business', 'premium'];

/**
 * Validar compatibilidad entre modalidad de streaming y plan.
 *
 * EXTERNAL_STREAM: cualquier plan activo es compatible.
 * RFW_MANAGED: solo planes que incluyan streaming administrado.
 */
export function checkPlanCompatibility(
  plan: BackendPlan,
  streamingMode: StreamingMode,
): PlanCompatibilityResult {
  if (!plan.is_active) {
    return { compatible: false, reason: 'Este plan no está disponible actualmente.' };
  }

  if (streamingMode === 'RFW_MANAGED') {
    const hasStreaming = plan.streaming_modes?.includes('RFW_MANAGED')
      || STREAMING_CAPABLE_PLAN_SLUGS.includes(plan.slug);

    if (!hasStreaming) {
      return {
        compatible: false,
        reason: 'Este plan no incluye streaming administrado. Seleccioná un plan con capacidad de streaming.',
      };
    }
  }

  // EXTERNAL_STREAM: todos los planes activos son compatibles
  return { compatible: true };
}

// ── CANCEL REQUEST ──

export interface CancelSubscriptionRequest {
  subscription_id: string;
  reason?: string;
}

export interface CancelSubscriptionResponse {
  subscription_id: string;
  status: string;
  message: string;
}

// ── ERROR HANDLING ──

export interface CheckoutError {
  code: string;
  message: string;
  status_code: number;
}

/**
 * Mapear códigos de estado HTTP a mensajes amigables.
 */
export function mapCheckoutError(statusCode: number, serverMessage?: string): CheckoutError {
  const map: Record<number, { code: string; message: string }> = {
    401: { code: 'SESSION_EXPIRED', message: 'Tu sesión expiró. Volvé a iniciar sesión para continuar.' },
    403: { code: 'FORBIDDEN', message: 'No tenés permiso para realizar esta operación.' },
    404: { code: 'PLAN_NOT_FOUND', message: 'El plan seleccionado no existe o fue descontinuado.' },
    409: { code: 'CHECKOUT_CONFLICT', message: serverMessage || 'El plan está inactivo o el checkout no es válido.' },
    422: { code: 'INCOMPATIBLE_PLAN', message: serverMessage || 'El plan no es compatible con tu modalidad de streaming.' },
    503: { code: 'PROVIDER_UNAVAILABLE', message: 'El procesador de pagos no está disponible. Intentá de nuevo en unos minutos.' },
  };

  const entry = map[statusCode];
  if (entry) return { ...entry, status_code: statusCode };

  return {
    code: 'UNKNOWN',
    message: serverMessage || 'Ocurrió un error inesperado. Intentá de nuevo.',
    status_code: statusCode,
  };
}

// ── CHECKOUT URL BUILDERS ──

/**
 * Construir success_url y cancel_url para el checkout.
 * El backend las valida contra una whitelist.
 */
export function buildCheckoutUrls(basePath: string): { success_url: string; cancel_url: string } {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = basePath ? `/${basePath}` : '';

  return {
    success_url: `${origin}${path}/admin/settings?checkout=success`,
    cancel_url: `${origin}${path}/admin/settings?checkout=cancelled`,
  };
}